import moment from "moment";

import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { PortfolioPortalSyncHistoryOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ICheckable } from "@/Common/Models/UI/Inputs/ICheckable";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { PortalSyncHistoryTableItem } from "@/Portfolio/Models/PortalSyncHistory";
import { IPortalSyncSettingsModel } from "@/Portfolio/Models/UI/IPortalSyncSettingsModel";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class PortalSyncHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_Reload, this.reload);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_CurrentPageChanged, this.changePage);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_SearchBoxTextChanged, this.searchTextChanged);

        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_SearchSettings_Open, this.searchFiltersPopupOpen);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_SearchSettings_OkClick, this.searchFiltersOkClick);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_SearchSettings_CancelClick, this.searchFiltersCancelClick);

        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_Column_SortClick, this.columnSortClick);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_ColumnOrder_PopupOpen, this.columnOrderPopupOpen);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_ColumnOrder_PopupClose, this.columnOrderPopupClose);

        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_PatientClick, this.onPatientClick);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_RowClick, this.onRowClick);

        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_SendToPortal_MessageChecked, this.onSendToPortalMessageCheckChanged);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_SendToPortal_RevokeClick, this.onSendToPortalRevokeClick);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_SendToPortal_SyncClick, this.onSendToPortalSyncClick);
        this.RegisterOperation(LocalOperations.Portfolio.PortalSync_SendToPortal_CancelClick, this.onSendToPortalCancelClick);
    }

    private reload(_: LocalEvent, store: PortfolioStore): void {
        store.portalSyncModel = store.portalSyncModel.set("CurrentRowOffset", 0);

        store.PortfolioActions.LoadPortalSyncHistory(store.portalSyncModel);
    }

    private changePage(localEvent: LocalEvent, store: PortfolioStore): void {
        store.portalSyncModel = store.portalSyncModel.set("CurrentRowOffset", localEvent.Get("Offset"));
        store.PortfolioActions.LoadPortalSyncHistory(store.portalSyncModel);
    }

    private searchTextChanged(localEvent: LocalEvent, store: PortfolioStore): void {
        store.portalSyncModel = store.portalSyncModel
            .set("Settings", store.portalSyncModel.get("Settings").set("SearchText", localEvent.Get("value")))
            .set("CurrentRowOffset", 0);

        store.PortfolioActions.LoadPortalSyncHistory(store.portalSyncModel);
    }

    private onPatientClick(request: LocalEvent): void {
        let item = request.Get<PortalSyncHistoryTableItem>("item");
        let encounterId = request.Get<string | null>("encounterId");

        window.setTimeout(() => {
            let openPatientOperation = new LocalEvent(LocalOperations.Misc.RedirectToTab);

            openPatientOperation.AddItem("value", item.Model.Patient.Id);
            openPatientOperation.AddItem("target", RedirectTo.Patient);
            openPatientOperation.AddItem("currentLocation", RouterHistory.location);
            openPatientOperation.AddItem("OpenEncounterId", encounterId);
            LocalEventDispatcher.dispatch(openPatientOperation);
        }, 100);
    }

    private onRowClick(request: LocalEvent, store: PortfolioStore): void {
        if (store.portalSyncModel.get("LoadingSharedItem"))
            return;

        const tableItem = request.Get<PortalSyncHistoryTableItem>("dataSourceItem");

        store.portalSyncModel = store.portalSyncModel
            .set("SelectedRow", tableItem)
            .set("LoadingSharedItem", true);

        store.PortfolioActions.loadSharedItem(
            tableItem.Model.SharedItemId,
            true,
            tableItem.Model.User.Id === sessionStore.getCurrentEmployee().ContactId
        );
    }

    private onSendToPortalCancelClick(_: LocalEvent, store: PortfolioStore): void {
        store.portalSyncModel = store.portalSyncModel
            .set("SyncToPortalPopupOpen", false)
            .set("SyncToPortalModel", null);
    }

    private onSendToPortalRevokeClick(_: LocalEvent, store: PortfolioStore): void {
        const model = store.portalSyncModel
            .get("SyncToPortalModel");

        if (!model) return;

        const sharedItem = model.get("SharedItem");

        if (!sharedItem) return;

        store.PortfolioActions.revokePortalSharedItem(sharedItem.SharedItemId);
    }

    private onSendToPortalSyncClick(localEvent: LocalEvent, store: PortfolioStore): void {
        const model = store.portalSyncModel
            .get("SyncToPortalModel");

        if (!model) return;

        const sharedItem = model.get("SharedItem");

        if (!sharedItem) return;

        let portalMessages: SharedItemMessage[] = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(model);

        const patientContactId = model.get("PatientContactId");
        const priority = localEvent.Get<SharedItemPriority>("priority");
        const canPatientReplyWithMessage = localEvent.Get<boolean>("canPatientReplyWithMessage");

        const messageText = localEvent.Get<string>("messageText");
        const hasTextMessage = !IsNullOrWhiteSpace(messageText);
        const hasPatientId = !IsNullOrWhiteSpace(patientContactId);
        const hasUnsentMsg = hasTextMessage && hasPatientId;

        // create new message if user has entered text in message textbox
        if (hasUnsentMsg) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(messageText, priority));
        }

        store.PortfolioActions.updateSharedItem(
            sharedItem.SharedItemId,
            sharedItem.SharedItemType,
            sharedItem.Subject,
            sharedItem.PatientContactId,
            portalMessages,
            localEvent.Get<boolean>("includeDocument"),
            canPatientReplyWithMessage
        );
    }

    private onSendToPortalMessageCheckChanged(event: LocalEvent, store: PortfolioStore): void {
        let checkedItem = event.Get<SharedItemMessage>("item");

        if (!checkedItem) return;

        store.portalSyncModel = store.portalSyncModel
            .set(
                "SyncToPortalModel",
                PatientHelper.handlePortalItemCheck(
                    store.portalSyncModel.get("SyncToPortalModel"),
                    checkedItem
                )
            );
    }

    private columnSortClick(localEvent: LocalEvent, store: PortfolioStore): void {
        let settings = store.portalSyncModel.get("Settings");

        const newSortColumn = localEvent.Get<PortfolioPortalSyncHistoryOrderType>("sortColumn");
        const currentSortColumn = settings.get("OrderByColumn");
        const currentSortDirection = settings.get("OrderByDirection");
        const newSortDirection = newSortColumn === currentSortColumn && currentSortDirection === SortDirection.ASC
            ? SortDirection.DESC
            : SortDirection.ASC;

        settings = settings
            .set("OrderByColumn", newSortColumn)
            .set("OrderByDirection", newSortDirection);

        store.portalSyncModel = store.portalSyncModel.set("Settings", settings);

        // sort server-side if there are more rows than the current page
        if (store.portalSyncModel.get("TotalRows") > settings.get("PageSize")) {
            store.PortfolioActions.LoadPortalSyncHistory(store.portalSyncModel);
            return;
        }

        // otherwise sort on the client
        // sorting logic changes need to be done in the MYLE C# handler as well
        let data = store.portalSyncModel.get("TableData").toArray();

        switch (newSortColumn) {
            case PortfolioPortalSyncHistoryOrderType.Status:
                if (newSortDirection === SortDirection.DESC)
                    data.sortByPropertyDesc(p => p.Model.SyncStatus);
                else
                    data.sortByProperty(p => p.Model.SyncStatus);
                break;

            case PortfolioPortalSyncHistoryOrderType.SyncDate:
                if (newSortDirection === SortDirection.DESC)
                    data.sortByPropertyDesc(p => p.Model.SyncDate);
                else
                    data.sortByProperty(p => p.Model.SyncDate);
                break;

            case PortfolioPortalSyncHistoryOrderType.ViewDate:
                if (newSortDirection === SortDirection.DESC)
                    data.sortByPropertyDesc(p => p.Model.ViewedDate ? p.Model.ViewedDate : moment("0000-01-01"));
                else
                    data.sortByProperty(p => p.Model.ViewedDate ? p.Model.ViewedDate : moment("9999-12-31"));
                break;
        }

        store.portalSyncModel = store.portalSyncModel.set("TableData", data.toImmutable());
    }

    private searchFiltersPopupOpen(_: LocalEvent, store: PortfolioStore): void {
        store.portalSyncModel = store.portalSyncModel.set("SearchFilterPopupOpen", true);
    }

    private searchFiltersOkClick(localEvent: LocalEvent, store: PortfolioStore): void {
        const currentSettings = store.portalSyncModel.get("Settings");
        let newSettings = localEvent.Get<IPortalSyncSettingsModel>("PortalSyncSettings");

        const searchFiltersDiffer = !this.dateSettingsAreEqual(currentSettings, newSettings) ||
            !this.checkListsAreEqual(currentSettings.get("Sites"), newSettings.get("Sites")) ||
            !this.checkListsAreEqual(currentSettings.get("Employees"), newSettings.get("Employees")) ||
            !this.checkListsAreEqual(currentSettings.get("ItemTypes"), newSettings.get("ItemTypes")) ||
            !this.checkListsAreEqual(currentSettings.get("Statuses"), newSettings.get("Statuses"));

        const pageSizeDiffers = currentSettings.get("PageSize") !== newSettings.get("PageSize");

        store.portalSyncModel = store.portalSyncModel
            .set("SearchFilterPopupOpen", false)
            .set("ColumnOrderPopupOpen", false);

        if (!searchFiltersDiffer && !pageSizeDiffers) return;

        newSettings = newSettings.set("SearchText", currentSettings.get("SearchText"));

        store.portalSyncModel = store.portalSyncModel
            .set("Settings", newSettings);

        if (searchFiltersDiffer)
            store.portalSyncModel = store.portalSyncModel.set("CurrentRowOffset", 0);

        store.PortfolioActions.LoadPortalSyncHistory(store.portalSyncModel);
    }

    private dateSettingsAreEqual(a: IPortalSyncSettingsModel, b: IPortalSyncSettingsModel): boolean {
        return (
            (
                a.get("DateFrom") === null && b.get("DateFrom") === null
                || a.get("DateFrom") === b.get("DateFrom")
            )
            && (
                a.get("DateTo") === null && b.get("DateTo") === null
                || a.get("DateTo") === b.get("DateTo")
            )
        );
    }

    private checkListsAreEqual(a: Immutable.List<ICheckable>, b: Immutable.List<ICheckable>): boolean {
        const aIds = a.filter(p => p.Checked).map(p => p.Id).toArray();
        const bIds = b.filter(p => p.Checked).map(p => p.Id).toArray();

        return aIds.length === bIds.length &&
            aIds.intersect(bIds).length === bIds.length;
    }

    private searchFiltersCancelClick(_: LocalEvent, store: PortfolioStore): void {
        store.portalSyncModel = store.portalSyncModel
            .set("SearchFilterPopupOpen", false);
    }

    private columnOrderPopupOpen(_: LocalEvent, store: PortfolioStore): void {
        store.portalSyncModel = store.portalSyncModel
            .set("SearchFilterPopupOpen", false)
            .set("ColumnOrderPopupOpen", true);

        sessionStore.showGlobalOverlay(true);
    }

    private columnOrderPopupClose(_: LocalEvent, store: PortfolioStore): void {
        store.portalSyncModel = store.portalSyncModel
            .set("ColumnOrderPopupOpen", false);

        sessionStore.hideGlobalOverlay();
    }
}

export { PortalSyncHandler };
